import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileSignature, Plus, Loader2, AlertCircle, FileText, History } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ModuleAccessDenied from '../../components/ui/ModuleAccessDenied';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import contractService from '../../services/contracts';
import { formatDateInTz, useOrgTimezone } from '../../utils/timezone';
import {
  CONTRACT_TIERS, CONTRACT_TIER_LABELS,
  type ContractFamily, type ContractTier,
} from '../../types/contracts';
import { LICENCE_STARTER_TEMPLATES } from './licenseTemplateStarters';

/**
 * Every contract the organisation sends, one row per contract (not per
 * version). Each says which version is in force and whether a draft of the
 * next one is waiting to be published.
 */
export default function ContractsList() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar', ['viewer', 'pbr', 'admin']);
  const { hasAccess: canWrite } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [families, setFamilies] = useState<ContractFamily[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [tier, setTier] = useState<ContractTier>('nursery_master_sublicence');
  const [starterKey, setStarterKey] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasAccess) return;
    contractService.list()
      .then(res => setFamilies(res.success ? (res.data ?? []) : []))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load contracts'));
  }, [hasAccess]);

  const starters = useMemo(() => LICENCE_STARTER_TEMPLATES.filter(s => s.license_tier === tier), [tier]);

  const byTier = useMemo(() => {
    const m = new Map<ContractTier, ContractFamily[]>();
    for (const f of families || []) {
      if (!m.has(f.license_tier)) m.set(f.license_tier, []);
      m.get(f.license_tier)!.push(f);
    }
    return CONTRACT_TIERS.filter(t => m.has(t)).map(t => ({ tier: t, rows: m.get(t)! }));
  }, [families]);

  const create = async () => {
    setCreateError(null);
    if (!name.trim()) { setCreateError('Give the contract a name'); return; }
    const starter = starters.find(s => s.key === starterKey);
    const body = starter ? starter.body_richtext : `# ${name.trim()}\n\nBetween {{licensor_org}} and {{licensee_org}}.\n`;
    setSaving(true);
    try {
      const res = await contractService.create({
        name: name.trim(), license_tier: tier, description: description.trim() || null, body_richtext: body,
      });
      if (res.success) navigate(`/cultivar/contracts/${res.data.id}`);
      else setCreateError(res.error.message);
    } catch (e: unknown) {
      setCreateError(e instanceof Error ? e.message : 'Could not create the contract');
    } finally {
      setSaving(false);
    }
  };

  if (licLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  }
  if (!hasAccess) return <ModuleAccessDenied moduleName="Cultivar" />;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Contracts"
        subtitle="Fixed, versioned agreements — only the published version is ever sent"
        icon={<FileSignature className="h-6 w-6 text-white" />}
        actionButtons={canWrite ? (
          <Button variant="outline" size="sm" onClick={() => setCreating(c => !c)}>
            <Plus className="h-4 w-4" /> New contract
          </Button>
        ) : undefined}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        <div className="bg-white border border-neutral-200 rounded-lg p-4 text-sm text-neutral-700 space-y-1">
          <p>
            A contract is written as a draft, then published by a cultivar admin. A published
            version never changes. To change the wording, start a new version, which replaces
            the old one only when it is published.
          </p>
          <p>
            Agreements still unsigned on an older version are withdrawn and re-sent on the new one.
            Agreements already signed stay on the version they were signed on.
          </p>
          <p>
            <Link to="/cultivar/contracts/history" className="text-primary-600 hover:underline inline-flex items-center gap-1">
              <History className="h-3.5 w-3.5" /> Everything that has happened to your contracts
            </Link>
          </p>
        </div>

        {creating && canWrite && (
          <Card>
            <CardHeader><CardTitle>New contract</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="contract-name" className="block text-sm font-semibold text-neutral-700 mb-1">Name *</label>
                <input
                  id="contract-name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Group A Master Sublicence"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="contract-tier" className="block text-sm font-semibold text-neutral-700 mb-1">What kind of agreement *</label>
                <select
                  id="contract-tier"
                  value={tier}
                  onChange={e => { setTier(e.target.value as ContractTier); setStarterKey(''); }}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                >
                  {CONTRACT_TIERS.map(t => <option key={t} value={t}>{CONTRACT_TIER_LABELS[t]}</option>)}
                </select>
                <p className="mt-1 text-xs text-neutral-500">
                  Fixed once created. A master contract can only be sent as a sublicence group’s master.
                </p>
              </div>
              <div>
                <label htmlFor="contract-starter" className="block text-sm font-semibold text-neutral-700 mb-1">Start from</label>
                <select
                  id="contract-starter"
                  value={starterKey}
                  onChange={e => setStarterKey(e.target.value)}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">A blank page</option>
                  {starters.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
                </select>
                {starterKey && (
                  <p className="mt-1 text-xs text-neutral-500">
                    {starters.find(s => s.key === starterKey)?.description}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="contract-desc" className="block text-sm font-semibold text-neutral-700 mb-1">Description</label>
                <textarea
                  id="contract-desc"
                  rows={2}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Who this contract is for, and when it is used"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              {createError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm flex gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /> <span>{createError}</span>
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
                <Button variant="primary" onClick={create} disabled={saving}>
                  {saving ? 'Creating…' : 'Create draft'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>
        ) : families === null ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>
        ) : families.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-sm text-neutral-500">
            No contracts yet.{canWrite ? ' Create one to start sending agreements.' : ''}
          </CardContent></Card>
        ) : (
          byTier.map(({ tier: t, rows }) => (
            <Card key={t}>
              <CardHeader><CardTitle>{CONTRACT_TIER_LABELS[t]}</CardTitle></CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y divide-neutral-100">
                  {rows.map(f => (
                    <li key={f.id}>
                      <Link to={`/cultivar/contracts/${f.id}`} className="block px-4 py-3 hover:bg-neutral-50">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-neutral-900 flex items-center gap-2 break-words">
                              <FileText className="h-4 w-4 text-neutral-400 flex-shrink-0" /> {f.name}
                            </p>
                            {f.cultivar_id && (
                              <p className="text-xs text-neutral-500">For {f.cultivar_trade_name || f.cultivar_name} only</p>
                            )}
                            <p className="text-xs text-neutral-600 mt-0.5">
                              {f.current_version
                                ? <>v{f.current_version} in force since {formatDateInTz(f.current_published_at!, orgTz)}</>
                                : <span className="text-amber-700">Not published — cannot be sent</span>}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-1.5 text-xs">
                            {f.draft_version && (
                              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Draft v{f.draft_version}</span>
                            )}
                            {f.pending_count > 0 && (
                              <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">{f.pending_count} awaiting signature</span>
                            )}
                            {f.signed_count > 0 && (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{f.signed_count} signed</span>
                            )}
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
